import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TitleHelmet from 'components/TitleHelmet';
import SyringeSVG from 'components/SyringeSVG'; // Assuming correct path
import { CalculatorInput, CalculatorWarning } from 'components/calc'; // Assuming correct path
import { useCalculatorStorage } from 'components/hooks/useCalculatorStorage'; // Assuming correct path
import Disclaimer from 'components/Disclaimer'; // Assuming correct path
import Analytics from 'services/analytics'; // Assuming correct path

import OtherCalcs from 'components/OtherCalcs';

/**
 * VialTransitionCalculator component helps users calculate medication dosages
 * when transitioning between two vials, potentially with different concentrations,
 * to achieve a desired total dosage.
 * It guides the user through a multi-step process:
 * 1. Input desired total dose, current vial concentration, and units planned to draw from current vial.
 * 2. If current vial is insufficient, input new vial concentration.
 * It then displays the amount drawn from the current vial and, if needed, the units to draw from the new vial.
 * It also handles scenarios where the current vial draw is sufficient or more than sufficient.
 */
const VialTransitionCalculator: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [finalMessage, setFinalMessage] = useState<string | React.ReactNode>('');

  const [desiredTotalDosage, setDesiredTotalDosage] = useCalculatorStorage({
    key: 'vt_desiredTotalDosage',
    initialValue: '',
  });
  const [currentVialConcentration, setCurrentVialConcentration] = useCalculatorStorage({
    key: 'vt_currentVialConcentration',
    initialValue: '',
  });
  const [currentVialUnitsDrawn, setCurrentVialUnitsDrawn] = useCalculatorStorage({
    key: 'vt_currentVialUnitsDrawn',
    initialValue: '',
  });
  const [newVialConcentration, setNewVialConcentration] = useCalculatorStorage({
    key: 'vt_newVialConcentration',
    initialValue: '',
  });

  const [mgFromCurrentVialDraw, setMgFromCurrentVialDraw] = useState<number | null>(null);
  const [unitsForDesiredDoseFromCurrentVial, setUnitsForDesiredDoseFromCurrentVial] = useState<number | null>(null);
  const [unitsFromNewVial, setUnitsFromNewVial] = useState<number | null>(null);

  // Scroll to top whenever results are shown
  useEffect(() => {
    if (showResults) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showResults]);
  /**
   * Handles the first step of the calculation.
   * Validates inputs for desired dosage, current vial concentration, and units drawn from current vial.
   * Calculates the medication amount (mg) from the current vial.
   * If sufficient, displays results (including adjustment instructions if overdrawn).
   * If insufficient, proceeds to step 2 for new vial information.
   */
  const handleStep1Continue = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setErrorMessage(null);
    setFinalMessage('');
    setShowResults(false); // Ensure results are hidden until explicitly shown

    const desiredMgValue = parseFloat(desiredTotalDosage);
    const currentConcValue = parseFloat(currentVialConcentration);
    const currentUnitsValue = parseFloat(currentVialUnitsDrawn);

    if (isNaN(desiredMgValue) || desiredMgValue <= 0) {
      setErrorMessage('Please enter a valid, positive number for desired total dosage.');
      return;
    }
    if (isNaN(currentConcValue) || currentConcValue <= 0) {
      setErrorMessage('Please enter a valid, positive number for current vial concentration.');
      return;
    }
    if (isNaN(currentUnitsValue) || currentUnitsValue <= 0) {
      setErrorMessage('Please enter a valid, positive number for units drawn from current vial.');
      return;
    }

    // Basic concentration warning (can be expanded)
    if (currentConcValue < 2 || currentConcValue > 50) {
      // Example range
      // This could be a CalculatorWarning component in the results,
      // but for now, an error message or a softer warning if we proceed.
      // For now, let's allow calculation but consider where to put formal warnings.
    }

    const drawnMg = (currentUnitsValue / 100) * currentConcValue;

    Analytics.track({
      category: 'VialTransitionCalculator',
      action: 'Step1Continue_Attempt',
      properties: {
        desiredDosage: desiredMgValue,
        currentConcentration: currentConcValue,
        currentUnitsDrawn: currentUnitsValue,
        calculatedMgFromDraw: drawnMg,
      },
    });

    if (drawnMg >= desiredMgValue) {
      // Scenario A: Current vial is sufficient
      setShowResults(true);
      const unitsActuallyNeededFromCurrent = (desiredMgValue / currentConcValue) * 100;

      if (currentUnitsValue > unitsActuallyNeededFromCurrent) {
        // Overage
        setUnitsForDesiredDoseFromCurrentVial(unitsActuallyNeededFromCurrent);
        setMgFromCurrentVialDraw(null); // Not strictly needed for this path, but good to clear
        setUnitsFromNewVial(null); // Clear new vial info
        setFinalMessage(
          <div>
            <CalculatorWarning message="Be careful! That's too much." />
            Hold up! The {currentUnitsValue} units from your current vial would provide {drawnMg.toFixed(2)}mg of
            medication. That's more than your desired dose of {desiredTotalDosage}mg, so you don't need to open that new
            vial just yet.
          </div>
        );
      } else {
        // Exact amount or very close (due to floating point)
        setUnitsForDesiredDoseFromCurrentVial(currentUnitsValue);
        setMgFromCurrentVialDraw(null);
        setUnitsFromNewVial(null);
        setFinalMessage(
          <>
            <p>
              Perfect! The {currentUnitsValue} units you drew from your current vial will provide your desired{' '}
              {desiredTotalDosage}mg of medication, so you don't need to open that new vial just yet.
            </p>
          </>
        );
      }
      Analytics.track({
        category: 'VialTransitionCalculator',
        action: 'Step1Continue_Sufficient',
        properties: {
          desiredDosage: desiredMgValue,
          currentConcentration: currentConcValue,
          currentUnitsDrawn: currentUnitsValue,
          calculatedMgFromDraw: drawnMg,
          unitsRecommended: unitsActuallyNeededFromCurrent,
        },
      });
    } else {
      // Scenario B: Current vial has some, but not enough
      setMgFromCurrentVialDraw(drawnMg);
      setUnitsForDesiredDoseFromCurrentVial(null); // Clear as not applicable
      setUnitsFromNewVial(null); // Clear new vial info for now
      setCurrentStep(2); // Proceed to next step for new vial details
      setShowResults(false); // Ensure results are not shown yet
      Analytics.track({
        category: 'VialTransitionCalculator',
        action: 'Step1Continue_Insufficient',
        properties: {
          desiredDosage: desiredMgValue,
          currentConcentration: currentConcValue,
          currentUnitsDrawn: currentUnitsValue,
          calculatedMgFromDraw: drawnMg,
        },
      });
    }
  };

  /**
   * Handles the second step of the calculation after new vial concentration is provided.
   * Validates the new vial concentration.
   * Calculates the remaining medication needed and the units to draw from the new vial.
   * Displays the final combined dosage instructions.
   */
  const handleStep2CalculateTotal = () => {
    setErrorMessage(null);

    const newConcValue = parseFloat(newVialConcentration);
    if (isNaN(newConcValue) || newConcValue <= 0) {
      setErrorMessage('Please enter a valid, positive number for new vial concentration.');
      return;
    }

    // Basic concentration warning (can be expanded)
    if (newConcValue < 2 || newConcValue > 50) {
      // Example range
      // Similar to Step 1, consider where to place formal warnings.
    }

    if (mgFromCurrentVialDraw === null || desiredTotalDosage === '') {
      setErrorMessage('Error: Missing data from previous step. Please start over.');
      // Potentially reset to step 1 or show a more guided error.
      return;
    }

    const desiredMgValue = parseFloat(desiredTotalDosage);
    const remainingMgNeeded = desiredMgValue - mgFromCurrentVialDraw;

    if (remainingMgNeeded <= 0) {
      // This case should ideally be caught by Step 1 logic, but as a safeguard:
      setErrorMessage(
        'No additional medication needed based on current vial draw. Please review inputs or start over.'
      );
      setShowResults(true); // Show an error message in the results area or handle differently
      setFinalMessage(
        <CalculatorWarning
          type="urgent"
          message="Calculation error: No additional medication appears to be needed. Please reset and check your inputs."
        />
      );
      return;
    }

    const calculatedUnitsFromNew = (remainingMgNeeded / newConcValue) * 100;
    setUnitsFromNewVial(calculatedUnitsFromNew);

    setFinalMessage(
      <>
        {/* From your current vial ({currentVialConcentration} mg/mL), the {currentVialUnitsDrawn} units you drew will be{' '}
        <strong>{mgFromCurrentVialDraw.toFixed(2)}mg</strong> of medication.
        <SyringeSVG calculatedVolume={parseFloat(currentVialUnitsDrawn) / 100} />
        <br />
        To get your desired total of {desiredTotalDosage}mg, you will need to add:
        <br />
        <div className="resultsText" style={{ marginTop: '10px', marginBottom: '10px' }}>
          <strong>{Math.round(calculatedUnitsFromNew)} units</strong> from your new {newVialConcentration}mg/mL vial.
        </div>
        <SyringeSVG calculatedVolume={calculatedUnitsFromNew / 100} />
        This will give you an additional {remainingMgNeeded.toFixed(2)}mg,  */}
        <strong>For a total dose of {desiredTotalDosage}mg:</strong>
      </>
    );
    setShowResults(true);

    Analytics.track({
      category: 'VialTransitionCalculator',
      action: 'Step2CalculateTotal_Success',
      properties: {
        desiredDosage: desiredMgValue,
        currentConcentration: parseFloat(currentVialConcentration), // ensure it's a number
        currentUnitsDrawn: parseFloat(currentVialUnitsDrawn), // ensure it's a number
        mgFromCurrentVial: mgFromCurrentVialDraw,
        newConcentration: newConcValue,
        remainingMgNeeded: remainingMgNeeded,
        calculatedUnitsFromNew: Math.round(calculatedUnitsFromNew),
      },
    });
  };

  /**
   * Resets all calculator inputs, state variables, and messages to their initial values,
   * returning the calculator to the first step.
   * Tracks a reset event for analytics.
   */
  const handleReset = () => {
    setDesiredTotalDosage('');
    setCurrentVialConcentration('');
    setCurrentVialUnitsDrawn('');
    setNewVialConcentration('');
    setMgFromCurrentVialDraw(null);
    setUnitsForDesiredDoseFromCurrentVial(null);
    setUnitsFromNewVial(null);
    setFinalMessage('');
    setErrorMessage(null);
    setCurrentStep(1);
    setShowResults(false);
    Analytics.track({
      category: 'VialTransitionCalculator',
      action: 'ResetForm',
      label: 'User reset the calculator form',
    });
  };

  return (
    <div className="App">
      {' '}
      {/* Assuming 'App' class for overall styling */}
      <TitleHelmet
        title="Vial Transition Dosage Calculator"
        description="Simple tool to help you transition between GLP-1 vials of different concentrations safely. Calculate the correct units from both vials to maintain your prescribed dose."
      />
      <header className="App-header">
        {' '}
        {/* Assuming 'App-header' for styling */}
        <h1>Vial Transition Dosage Calculator</h1>
      </header>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {/* Placeholder for additional warnings based on input values */}
      {/* Example: High dosage warning */}
      {showResults && (
        <>
          {parseFloat(desiredTotalDosage) > 15 && (
            <CalculatorWarning
              type="urgent"
              message={`A total dose of ${desiredTotalDosage}mg is unusually high. Please verify your prescription and calculations carefully.`}
            />
          )}
          {(parseFloat(currentVialConcentration) > 30 ||
            (newVialConcentration !== '' && parseFloat(newVialConcentration) > 30)) && (
            <CalculatorWarning
              message={`A concentration above 30mg/mL is unusually high. Please double-check your vial label(s) to confirm this concentration is correct.`}
            />
          )}
          {(parseFloat(currentVialConcentration) < 7 ||
            (newVialConcentration !== '' && parseFloat(newVialConcentration) < 7)) && (
            <CalculatorWarning
              type="urgent"
              message={`A concentration below 7mg/mL is unusually low. Normal concentration is generally between 7mg/mL and 30mg/mL. Please double-check your vial label(s) to confirm this concentration is correct to avoid the risk of overdose.`}
            />
          )}
        </>
      )}
      {!showResults && currentStep === 1 && (
        <div className="calculator-container">
          <p>
            Almost done with your current vial, and switching to a new vial with a different concentration? We got you!
            This calculator will help you figure out how much to draw from each to get your desired total dose.
          </p>
          <p>First, enter the details for your current vial and desired total dosage:</p>
          <CalculatorInput
            id="desiredTotalDosage"
            value={desiredTotalDosage}
            onChange={setDesiredTotalDosage}
            unit="mg"
            placeholder="e.g., 5"
            label="Desired Total Dosage (mg)"
          />
          <CalculatorInput
            id="currentVialConcentration"
            value={currentVialConcentration}
            onChange={setCurrentVialConcentration}
            unit="mg/mL"
            placeholder="e.g., 10"
            label="Current Vial Concentration (mg/mL)"
          />
          <CalculatorInput
            id="currentVialUnitsDrawn"
            value={currentVialUnitsDrawn}
            onChange={setCurrentVialUnitsDrawn}
            unit="units"
            placeholder="e.g., 25"
            label="Units you can draw from your current vial"
          />
          <button onClick={handleStep1Continue} className="calculate-button">
            Continue
          </button>
        </div>
      )}
      {!showResults && currentStep === 2 && (
        <>
          <span>
            &#9664;{' '}
            <Link className="smaller-text" onClick={handleReset} to="">
              Reset calculator and start again
            </Link>
          </span>
          <div className="calculator-container">
            <p>
              From your current vial ({currentVialConcentration} mg/mL), the {currentVialUnitsDrawn} units you plan to
              draw will provide <strong>{mgFromCurrentVialDraw?.toFixed(2)}mg</strong> of medication.
            </p>
            <p>
              You need an additional{' '}
              <strong>{(parseFloat(desiredTotalDosage) - (mgFromCurrentVialDraw || 0)).toFixed(2)}mg</strong> to reach
              your desired total of {desiredTotalDosage}mg.
            </p>
            <CalculatorInput
              id="newVialConcentration"
              value={newVialConcentration}
              onChange={setNewVialConcentration}
              unit="mg/mL"
              placeholder="e.g., 20"
              label="What's the concentration on your new vial? (mg/mL)"
            />
            <p>
              <Link to="/faq/finding-concentration" target="_blank" className="help-link">
                Need more help finding your concentration? Click here for our detailed guide with examples.
              </Link>
            </p>
            <button onClick={handleStep2CalculateTotal} className="calculate-button">
              Calculate Total Dosage
            </button>
          </div>
        </>
      )}
      {showResults && (
        <div className="result">
          {finalMessage}
          {/* Syringe display logic */}
          {unitsForDesiredDoseFromCurrentVial !== null && unitsFromNewVial === null && (
            // Scenario A: Current vial sufficient (either exact or overage, adjusted dose shown)
            <>
              <div className="resultsText centered">
                Draw <strong>{unitsForDesiredDoseFromCurrentVial.toFixed(0)} units</strong> from your current{' '}
                {currentVialConcentration}mg/mL vial. <br />
                <SyringeSVG calculatedVolume={unitsForDesiredDoseFromCurrentVial / 100} />
                This provides your desired {desiredTotalDosage}mg.
              </div>
            </>
          )}
          {mgFromCurrentVialDraw !== null && unitsFromNewVial !== null && (
            // Scenario B: New vial needed
            <>
              <div style={{ marginTop: '30px' }}>
                <strong>From Current Vial:</strong>
                <div className="resultsText">
                  {currentVialUnitsDrawn} units from your current {currentVialConcentration}mg/mL vial provides{' '}
                  {mgFromCurrentVialDraw.toFixed(2)}mg.
                </div>
                <SyringeSVG calculatedVolume={parseFloat(currentVialUnitsDrawn) / 100} />
              </div>
              <div>
                <strong>From New Vial:</strong>
                <div className="resultsText">
                  Add <strong>{Math.round(unitsFromNewVial)} units</strong> from your new {newVialConcentration}mg/mL
                  vial for an additional {(parseFloat(desiredTotalDosage) - mgFromCurrentVialDraw).toFixed(2)}mg.
                </div>
                <SyringeSVG calculatedVolume={unitsFromNewVial / 100} />
              </div>
              <p className="resultsText centered">This brings your total dose to {desiredTotalDosage}mg.</p>
            </>
          )}

          <button onClick={handleReset} className="calculate-button" style={{ marginTop: '20px' }}>
            Reset Calculator & Start Over
          </button>
        </div>
      )}
      <Disclaimer />
      <OtherCalcs />
    </div>
  );
};

export default VialTransitionCalculator;
